import React, { useState, useEffect, useMemo } from "react";
import IconButton from "components/common/iconButton";
import { Card, Breadcrumb, Col, Row, Spinner, Tab, Tabs, Table, Modal, Button, Form } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import AdvanceTablePagination from "components/common/advance-table/advanceTablePagination";
import AdvanceTableFooter from 'components/common/advance-table/advanceTableFooter';
import useAdvanceTable from "hooks/useAdvanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import FalconCloseButton from "components/common/falconCloseButton";
import { toast } from "react-toastify";
import "assets/css/site.css";
import axios from "axios"; // Import Axios
import paths from "routes/paths";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import { fetchRoleAndClaims, handleLoadingAndAccess,getInitialPageSize, formatCreatedBy } from "../../assets/js/common";

const TaskDetails = () => {
    const locationData = useLocation();
    const [loading, setLoading] = useState(false); // State for loading
    const taskIdRaw = locationData.pathname.split("/")[2];
    const taskId= taskIdRaw ? taskIdRaw.toUpperCase() : null;
    const navigate = useNavigate(); // Initialize useNavigate hook
    const { t, i18n } = useTranslation(); // Access translation function
    const [isLoading, setIsLoading] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);
    const [taskData, setTaskData] = useState([]); // State for loading
    const [subtaskData, setSubtaskData] = useState([]); // State for loading
    const [selectedSubtask, setSelectedSubtask] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const [showTaskDeleteModal, setShowTaskDeleteModal] = useState(false);
    const [userList, setUserList] = useState([]);

    const handleEditTaskDetails = (taskId) => {
        navigate(`${paths.editTask.replace(":taskId", taskId.toLowerCase())}`);
    };

    const fetchUser = async () => {
        setIsLoading(true);
        const selectedCustomerId = localStorage.getItem("customerId");

        if (!selectedCustomerId) {
          setIsLoading(false);
          console.error("CustomerId is missing");
          return;
        }

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
            { 
                params: { customerId: selectedCustomerId },
                headers: {
                    Authorization: process.env.REACT_APP_BasicAuthSecret
                }
            }
          );
          setUserList(response.data); // assume response.data is an array of user objects
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "workTasks",
            claimValue: "view",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    // Fetch Task
    const fetchTask = async (taskId) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/taskRoute/getTaskById/${taskId}`,{
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );
            setTaskData(response.data);
        } catch (error) {
            console.error("Error fetching task data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch Subtasks
    const fetchSubtask = async (taskId) => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${taskId}`,{
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret
                    }
                }
            );
            setSubtaskData(response.data);
        } catch (error) {
            console.error("Error fetching subtask data:", error);
        }
    };

    useEffect(() => {
        fetchTask(taskId);
        fetchSubtask(taskId);
        fetchUser()

    }, [taskId]);

    const combinedData = useMemo(() => {
        const filtered = subtaskData
            .filter((subtask) => {
                const filter = globalFilter.toLowerCase();
                const subtaskText = subtask.Subtask?.toLowerCase() || "";
                const createdByUser = userList.find(user => user.UserId === subtask.CreatedBy);
                const createdByName = createdByUser?.Name?.toLowerCase() || "";

                return (
                    subtaskText.includes(filter) ||
                    createdByName.includes(filter)
                );
            })
            .map((subtask, index) => {
                const createdByUser = userList.find(user => user.UserId === subtask.CreatedBy);
                return {
                    Index: index + 1,
                    SubtaskId: subtask.SubtaskId,
                    CreatedBy: createdByUser?.Name || formatCreatedBy(subtask.CreatedBy), // show name or "-"
                    Subtask: subtask.Subtask,
                };
            });

        return filtered;
    }, [subtaskData, globalFilter, userList]);

    const handleRowClick = (row) => {
        setSelectedSubtask(row);
        setShowModal(true);
    };

    const columns = [
        {
            accessorKey: "index",
            header: t("index"),
            cell: ({ row }) => <span>{row.original.Index}</span>
        },
        {
            accessorKey: "subTask",
            header: t("subTasks"),
            cell: ({ row }) => <span>{row.original.Subtask}</span>
        },
        {
            accessorKey: "createdby",
            header: t("createdByWithoutcolon"),
            cell: ({ row }) => <span>{row.original.CreatedBy}</span>
        }
    ];

    const table = useAdvanceTable({
        data: combinedData,
        columns,
        selection: false,
        sortable: true,
        pagination: true,
        perPage: getInitialPageSize('taskDetailsTable')
    });

    // const handleTaskDelete = async () => {
    //     try {
    //         await axios.delete(`${process.env.REACT_APP_API_URL}/taskRoute/deleteTaskById/${taskId}`);
    //         toast.success(t("taskAndSubtasksDeletedSuccessMessage"));

    //         navigate("/task");
    //     } catch (error) {
    //         console.error("Failed to delete task:", error);
    //         toast.error(t("taskDeleteFailedMessage"));
    //     }
    // };

    // const TaskDeleteModal = ({ show, handleClose, handleDelete }) => (
    //     <Modal show={show} onHide={handleClose} backdrop="static">
    //         <Modal.Header>
    //             <Modal.Title>{t("confirmation")}</Modal.Title>
    //             <FalconCloseButton onClick={handleClose} />
    //         </Modal.Header>
    //         <Modal.Body>{t("confirmDeleteTask")}</Modal.Body>
    //         <Modal.Footer>
    //             <Button variant="falcon-default" onClick={handleClose}>
    //                 {t("cancel")}
    //             </Button>
    //             <Button variant="primary" onClick={handleDelete}>
    //                 {t("delete")}
    //             </Button>
    //         </Modal.Footer>
    //     </Modal>
    // );

    const loadingOrRedirect = handleLoadingAndAccess(
        isLoading,
        hasAccess,
    );

    if (loadingOrRedirect) {
        return loadingOrRedirect; // Render the loading spinner or handle navigation
    }

    return (
        <>
            <Row className="g-3 mb-3">
                <Col lg={12} xxl={12}>
                    <Card className="mb-3 h-100">
                        <Background image={corner4} className="bg-card" />
                        <Card.Body className="position-relative pe-3 pb-2">
                            <Row className="flex-between-center">
                                <Col md>
                                    <h5 className="mb-2 mb-md-0">{t("details")}</h5>
                                    <Breadcrumb>
                                        <Breadcrumb.Item
                                            as={Link}
                                            to="/task"
                                            className="fw-semi-bold mb-1 fs-9 data-font-colour"
                                            active
                                        >
                                            {t("task")}
                                        </Breadcrumb.Item>
                                        <Breadcrumb.Item className="fw-bold mb-1 fs-9 data-font-colour" active>
                                            {taskData.TaskName}
                                        </Breadcrumb.Item>
                                    </Breadcrumb>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            {loading ? (
                <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("loading")}</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <Row className="mb-3 g-3">
                        <Col md={12}>
                            <Card>
                                <Card.Header>
                                    <Row className="align-items-center">
                                        <Col>
                                            <h5 className="mb-0">{t("taskDetails")}</h5>
                                        </Col>
                                        <Col xs="auto">

                                            <IconButton
                                                className="ms-2"
                                                iconClassName="fs--2 me-1"
                                                variant="primary"
                                                size="sm"
                                                icon="pencil-alt"
                                                onClick={() => handleEditTaskDetails(taskId)}
                                            >
                                                {t("edit")}
                                            </IconButton>

                                            {/* <IconButton
                                                className="ms-2"
                                                iconClassName="fs--2 me-1"
                                                variant="danger"
                                                size="sm"
                                                icon="trash"
                                                onClick={() => setShowTaskDeleteModal(true)}
                                            >
                                                {t("delete")}
                                            </IconButton> */}
                                        </Col>
                                    </Row>
                                </Card.Header>
                                <Card.Body className="bg-light border-top">
                                    <AdvanceTableProvider {...table} className="mt-4 ">
                                        <Card className="mb-3 no-shadow">
                                            <Card.Header>
                                                <Row className="align-items-center justify-content-between">
                                                    {/* Task Name on the left */}
                                                    <Col xs="auto" className="fs-9 data-font-colour">
                                                        <strong>{t("taskName")}:</strong> {taskData?.TaskName}
                                                    </Col>

                                                    {/* Search Box on the right */}
                                                    <Col xs="auto" className="text-end">
                                                        <AdvanceTableSearchBox
                                                            setGlobalFilter={setGlobalFilter}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                {loading ? ( // Show loading spinner while fetching data
                                                   <div className="text-center py-5">
                                                        <Spinner animation="border" role="status">
                                                            <span className="visually-hidden">{t("loading")}</span>
                                                        </Spinner>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <AdvanceTable
                                                            headerClassName="bg-200 text-nowrap align-middle text-center"
                                                            rowClassName="align-middle white-space-nowrap"
                                                            tableProps={{
                                                                bordered: true,
                                                                striped: true,
                                                                size: "sm",
                                                                className: "mb-0 overflow-hidden data-font-size table-hover",
                                                            }}

                                                        />
                                                        {combinedData.length === 0 ? (
                                                            <div className="text-center pt-4">
                                                                {t("noDataAvailableText")}
                                                            </div>
                                                        ) : null}
                                                    </>
                                                )}
                                            </Card.Body>
                                            <Card.Footer>
                                                {/* <AdvanceTablePagination /> */}
                                                <AdvanceTableFooter
                                                    rowInfo
                                                    navButtons
                                                    rowsPerPageSelection
                                                    tableKey="taskDetailsTable"
                                                />
                                            </Card.Footer>
                                        </Card>
                                    </AdvanceTableProvider>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
            {/* <TaskDeleteModal
                show={showTaskDeleteModal}
                handleClose={() => setShowTaskDeleteModal(false)}
                handleDelete={handleTaskDelete}
            /> */}
        </>
    );
};

export default TaskDetails;